import { CheckCircle2, Info, Route } from "lucide-react";
import { Link } from "react-router-dom";
import { EmptyState } from "@/components/empty-state";
import { Button } from "@/components/ui/button";

interface PagesZeroUrlsEmptyStateProps {
  projectId: string;
}

export function PagesZeroUrlsEmptyState({
  projectId,
}: PagesZeroUrlsEmptyStateProps) {
  return (
    <EmptyState
      icon={Route}
      title="No page routes recorded yet"
      description="Walkthrough steps record URLs automatically as personas navigate your application. Once runs report page URLs, findings will appear grouped by route."
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild size="sm">
            <Link to={`/projects/${projectId}/scenarios`}>
              Run a walkthrough
            </Link>
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to={`/projects/${projectId}/settings`}>
              Configure route patterns
            </Link>
          </Button>
        </div>
      }
    />
  );
}

interface PageRouteCleanEmptyStateProps {
  routeKey: string;
  totalFindings?: number;
}

export function PageRouteCleanEmptyState({
  routeKey,
  totalFindings = 0,
}: PageRouteCleanEmptyStateProps) {
  return (
    <EmptyState
      icon={CheckCircle2}
      title="No open problems on this route"
      description={
        totalFindings > 0
          ? `${totalFindings} finding${totalFindings === 1 ? "" : "s"} observed on ${routeKey} have been resolved, verified, or dismissed.`
          : `Personas have walked ${routeKey} without encountering open blocking issues.`
      }
      variant="compact"
      unframed
      className="py-12"
    />
  );
}

interface PagesCoverageBannerProps {
  projectId: string;
  stepsWithUrl: number;
  totalSteps: number;
  coverageShare: number;
}

export function PagesCoverageBanner({
  projectId,
  stepsWithUrl,
  totalSteps,
  coverageShare,
}: PagesCoverageBannerProps) {
  if (totalSteps === 0 || coverageShare >= 1) return null;

  const percentage = Math.round(coverageShare * 100);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border/80 bg-muted/30 px-3.5 py-2.5 text-xs text-muted-foreground">
      <div className="flex items-center gap-2">
        <Info className="h-4 w-4 shrink-0 text-foreground" />
        <span>
          <strong className="font-semibold text-foreground">
            {percentage}%
          </strong>{" "}
          of steps carry a page URL ({stepsWithUrl} of {totalSteps}). Older runs
          or steps without URLs are excluded from route mapping.
        </span>
      </div>
      <Link
        to={`/projects/${projectId}/settings`}
        className="shrink-0 font-medium text-foreground underline hover:text-primary"
      >
        Manage route patterns
      </Link>
    </div>
  );
}
